import { ArrowForward, ShoppingCart } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import AddToBasketConfirmationDialog from '@/components/AddToBasketConfirmationDialog/AddToBasketConfirmationDialog';
import CardGrid from '@/components/CardGrid/CardGrid';
import ContactFormDialog from '@/components/ContactFormDialog/ContactFormDialog';
import Card from '@/components/ui/PriceCatalog/Card';
import getConfig from '@/configs/global';
import {
  priceCatalogProductsPropType,
  priceCatalogRequestTypePropType,
} from '@/constants/propTypes';
import {
  RENTAL_PRICE_BASIS,
  RENTAL_PRICE_BASIS_TRANSLATION_KEYS,
} from '@/constants/rentalPrice';
import {
  PRICE_CATALOG_REQUEST_TYPES,
  RENTAL_PRODUCTS_PATH,
} from '@/helpers/constants';
import { useBasketContext } from '@/hooks/useBasketContext';
import useFormatMessage from '@/hooks/useFormatMessage';
import useFormatPrice from '@/hooks/useFormatPrice';
import useFormatProductAttributeValue from '@/hooks/useFormatProductAttributeValue';
import { useGenericContactFormContext } from '@/hooks/useGenericContactFormContext';

import productPlaceholder from './lib/product-placeholder.png';

const {
  publicRuntimeConfig: { FEAT_FLAG_ATTRIBUTE_RANGES },
} = getConfig();

/**
 * PriceCatalog.
 */
const PriceCatalog = ({
  products,
  'data-test-subject': dataTestSubject,
  productGroupName: parentProductGroupName,
  productGroupId: parentProductGroupId,
  loading,
  requestType = 'rental_request',
}) => {
  const { addRequestToBasket, machineAlreadyInBasket } = useBasketContext();
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const {
    contactFormData,
    resetContactFormData,
    updateContactFormFieldData,
  } = useGenericContactFormContext();
  const router = useRouter();
  const formatMessage = useFormatMessage();
  const formatPrice = useFormatPrice();
  const formatValue = useFormatProductAttributeValue();

  const [duplicatedMachineData, setDuplicatedMachineData] = useState(null);

  const isPurchaseRequest =
    requestType === PRICE_CATALOG_REQUEST_TYPES.PURCHASE_REQUEST;

  const getMessageBody = productName =>
    formatMessage('pricecatalog_email_body', {
      productName,
    });

  const handleCloseContactForm = () => setContactFormOpen(false);

  const handleAddToBasket = request => {
    addRequestToBasket(request);
    router.push('/basket');
  };

  const getPriceText = (price, priceBasis) => {
    if (!price) return '';
    if (priceBasis === RENTAL_PRICE_BASIS.UNIT) {
      return `<span data-test-subject="price">${formatMessage(
        'rental_price_unit',
        {
          price: formatPrice(price),
        }
      )}</span>`;
    } else {
      return `<span data-test-subject="price">${formatMessage(
        'rental_price_from',
        {
          price: formatPrice(price),
        }
      )}</span> <span data-test-subject="price-basis">${formatMessage(
        priceBasis
          ? RENTAL_PRICE_BASIS_TRANSLATION_KEYS[priceBasis]
          : 'rental_price_basis_day'
      )}</span>`;
    }
  };

  const getSubtitle = ({ initialPrice, price, priceBasis }) => {
    const initialPriceText = initialPrice
      ? formatMessage('rental_price_from', {
          price: formatPrice(initialPrice),
        })
      : '';

    const priceText = !price
      ? formatMessage('rental_price_placeholder')
      : getPriceText(price, priceBasis);

    if (initialPriceText) {
      return (
        <Stack
          direction="row"
          alignItems="baseline"
          flexWrap="wrap"
          columnGap={1}
          data-test-subject="subtitle">
          <Typography
            variant="body1Redesign"
            component="div"
            color="grayscale.500"
            sx={{ textDecoration: 'line-through' }}
            dangerouslySetInnerHTML={{ __html: initialPriceText }}
            data-test-subject="initial-price"
          />
          {priceText && (
            <Typography
              variant="h6Redesign"
              component="div"
              color="primary.main"
              dangerouslySetInnerHTML={{ __html: priceText }}
            />
          )}
        </Stack>
      );
    }

    return priceText;
  };

  const getCards = () => {
    if (!Array.isArray(products)) {
      return [];
    }
    return products.map((product, index) => {
      const {
        headline,
        attributes,
        price,
        initialPrice,
        image,
        salesforceId,
        productReferenceId,
        automatedOffer,
        sendAutomatedOffer,
        minLeadTime,
        rentalPriceBasis,
        rentalPrice,
        priceBasis,
        productGroupName,
        productGroupId,
        slug,
        /*
        The URL is available only for custom product tiles
        and should direct to a custom product details page.
        */
        url,
      } = product;

      const handleOpenContactForm = () => {
        setContactFormOpen(true);
        updateContactFormFieldData('message', getMessageBody(headline));
      };

      const handleRentalRequestClick = () => {
        const request = {
          rentalObject: {
            name: headline,
            productReferenceId,
            salesforceId,
            productGroupName: productGroupName ?? parentProductGroupName,
            productGroupId: productGroupId ?? parentProductGroupId,
            automatedOffer,
            sendAutomatedOffer,
            minLeadTime,
            rentalPriceBasis,
            rentalPrice,
            invoiceBasis: priceBasis,
          },
          count: 1,
          rentalHours: null,
        };

        if (machineAlreadyInBasket(request.rentalObject)) {
          setDuplicatedMachineData(request);
        } else {
          handleAddToBasket(request);
        }
      };

      const subtitle = getSubtitle({
        initialPrice,
        price,
        priceBasis,
      });

      const description =
        attributes?.length && FEAT_FLAG_ATTRIBUTE_RANGES
          ? `<p>${attributes
              .map(
                ({ name, value, unit }) =>
                  `<span>${name}: ${formatValue(value, unit)}</span><br/>`
              )
              .join('')}</p>`
          : '';

      const requestNowButtonLabel = (
        <>
          {formatMessage('price_tile_request_now').toUpperCase()}
          <ArrowForward fontSize="small" sx={{ ml: 1 }} />
        </>
      );

      const learnMoreButtonLabel = (
        <>
          {formatMessage('price_tile_learn_more').toUpperCase()}
          <ArrowForward fontSize="small" sx={{ ml: 1 }} />
        </>
      );

      const addToBasketButtonLabel = (
        <>
          <ShoppingCart fontSize="small" sx={{ mr: 1 }} />
          {formatMessage('price_tile_add_to_basket').toUpperCase()}
        </>
      );

      const getActionConfig = () => {
        if (isPurchaseRequest) {
          return url
            ? {
                mainActionLink: url,
                mainActionButtonLabel: learnMoreButtonLabel,
                secondActionOnClick: handleOpenContactForm,
                secondActionButtonLabel: requestNowButtonLabel,
              }
            : {
                mainActionOnClick: handleOpenContactForm,
                mainActionButtonLabel: requestNowButtonLabel,
              };
        }

        if (slug) {
          return {
            mainActionLink: `${RENTAL_PRODUCTS_PATH}/${slug}`,
            mainActionButtonLabel: learnMoreButtonLabel,
            secondActionOnClick: salesforceId
              ? handleRentalRequestClick
              : undefined,
            secondActionButtonLabel: addToBasketButtonLabel,
          };
        }

        return {
          mainActionOnClick: salesforceId
            ? handleRentalRequestClick
            : undefined,
          mainActionButtonLabel: addToBasketButtonLabel,
        };
      };

      const getImage = () => {
        if (image === null) {
          return productPlaceholder.src;
        } else if (typeof image === 'string') {
          return image;
        } else {
          return { data: image };
        }
      };

      return {
        headline,
        subtitle,
        showSale: !!initialPrice,
        image: getImage(),
        description,
        sendAutomatedOffer,
        'data-test-subject': `price-catalog-tile-${index}`,
        ...getActionConfig(),
      };
    });
  };

  const cards = getCards();

  return (
    <>
      <CardGrid
        centerImage
        headingVariant="h5"
        showLinkText
        cards={cards}
        data-test-subject={dataTestSubject}
        loading={loading}
        cardRenderer={(card, idx) => (
          <Card card={card} key={`${card.headline}-${idx}`} />
        )}
      />
      <AddToBasketConfirmationDialog
        open={!!duplicatedMachineData}
        onAddAgain={() => {
          handleAddToBasket(duplicatedMachineData);
        }}
        onDontAddAgain={() => {
          router.push('/basket');
        }}
        machineName={duplicatedMachineData?.rentalObject.name}
      />
      {isPurchaseRequest && (
        <ContactFormDialog
          open={contactFormOpen}
          defaultValues={contactFormData}
          onClose={handleCloseContactForm}
          onChange={updateContactFormFieldData}
          onEmailSent={resetContactFormData}
        />
      )}
    </>
  );
};

PriceCatalog.propTypes = {
  products: priceCatalogProductsPropType.isRequired,
  requestType: priceCatalogRequestTypePropType.isRequired,
  'data-test-subject': PropTypes.string,
  productGroupName: PropTypes.string,
  productGroupId: PropTypes.number,
  loading: PropTypes.bool,
};

PriceCatalog.defaultProps = {
  'data-test-subject': 'price-catalog',
  productGroupName: null,
  loading: false,
};

export default PriceCatalog;
